import { useEffect, useRef, useState } from "react";
import { Bike, ChevronDown, MapPin, MessageSquareText, Minus, PersonStanding, ShoppingBag, Utensils, UtensilsCrossed, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ProductPicker } from "@/components/ProductPicker";
import { OptionsDialog } from "@/components/OptionsDialog";
import { OrderCustomerBox, loyaltyCalc, NO_REDEEM } from "@/components/delivery/OrderCustomerBox";
import { CustomerSearch } from "@/components/delivery/neworder/CustomerSearch";
import { AddressDialog, addrText } from "@/components/delivery/neworder/AddressDialog";
import { NewOrderCart, CartFooter } from "@/components/delivery/neworder/NewOrderCart";
import { OrderPayDialog } from "@/components/delivery/neworder/OrderPayDialog";
import { useAuth } from "@/context/AuthContext";
import { api, brl, errMsg } from "@/lib/api";
import { cn } from "@/lib/utils";

const TYPES = [["retirada", "Retirada", PersonStanding], ["delivery", "Delivery", Bike], ["local", "Consumo no local", Utensils], ["mesa", "Mesa", UtensilsCrossed]];
const EMPTY = { customer: { name: "", phone: "" }, type: "delivery", table_number: "", notes: "" };
const NO_LOC = { addr: {}, point: null, quote: null };
const norm = (s) => String(s || "").trim().toLowerCase();

function AddressRow({ addr, quote, onOpen }) {
  if (!addr.street && !addr.city) {
    return <button type="button" onClick={onOpen} className="flex h-14 w-full items-center gap-3 rounded-md border border-dashed border-primary px-4 text-sm font-semibold text-primary transition-colors hover:bg-primary/5" data-testid="order-address-btn"><MapPin className="h-5 w-5" />Informar endereço de entrega</button>;
  }
  return (
    <button type="button" onClick={onOpen} className="flex w-full items-start gap-3 rounded-md border p-3 text-left text-sm transition-colors hover:bg-accent" data-testid="order-address-summary">
      <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
      <span className="flex-1">
        <span className="text-muted-foreground">{addrText(addr)}</span>
        {quote && <span className={cn("block text-xs font-medium", quote.ok ? "text-emerald-600" : "text-red-500")}>{quote.ok ? `${quote.area} · taxa ${brl(quote.fee)}` : quote.message}</span>}
      </span>
      <span className="text-xs font-semibold text-primary">Alterar</span>
    </button>
  );
}

function NotesBox({ notes, setNotes }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-md border">
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex h-14 w-full items-center gap-3 px-4 text-sm" data-testid="order-notes-toggle">
        <MessageSquareText className="h-5 w-5 shrink-0 text-muted-foreground" />
        <span className="flex-1 truncate text-left">Observação do pedido{notes && !open ? `: ${notes}` : ""}</span>
        <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform", open && "rotate-180")} />
      </button>
      {open && <div className="px-3 pb-3"><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} autoFocus placeholder="Ex.: tocar o interfone, sem talheres" data-testid="order-notes-input" /></div>}
    </div>
  );
}

const HeadBtn = ({ onClick, label, testId, children }) => <button type="button" onClick={onClick} aria-label={label} title={label} className="grid h-9 w-9 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground" data-testid={testId}>{children}</button>;

function DraftPill({ count, total, onOpen }) {
  return (
    <button type="button" onClick={onOpen} className="fixed bottom-6 right-6 z-40 flex items-center gap-3 rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground shadow-lg transition-transform hover:scale-[1.03]" data-testid="order-draft-pill">
      <ShoppingBag className="h-4 w-4" />Pedido em andamento · {count} {count === 1 ? "item" : "itens"} · {brl(total)}
    </button>
  );
}

const NEEDS_CUSTOMER = ["delivery", "retirada"];
const blockOf = (items, f, needAddr) => (!items.length ? "Adicione produtos ao carrinho" : NEEDS_CUSTOMER.includes(f.type) && !f.customer.name.trim() ? "Escolha ou cadastre o cliente (obrigatório para delivery e retirada)" : needAddr ? "Informe o endereço de entrega" : "");
const nameOf = (f) => f.customer.name.trim() || (f.type === "mesa" ? `Mesa ${f.table_number}`.trim() : "Consumo no local");

export function NewOrderDialog({ open, onOpenChange, onCreated }) {
  const { session } = useAuth();
  const st = session.tenant.settings;
  const mapArea = st.delivery_area_mode === "mapa";
  const [f, setF] = useState(EMPTY);
  const [items, setItems] = useState([]);
  const [picking, setPicking] = useState(null);
  const [known, setKnown] = useState(null);
  const [redeem, setRedeem] = useState(NO_REDEEM);
  const [addr, setAddr] = useState({});
  const [loc, setLoc] = useState(NO_LOC);
  const [addrOpen, setAddrOpen] = useState(false);
  const [paying, setPaying] = useState(false);
  const payRef = useRef(null);
  const reset = () => { setF(EMPTY); setItems([]); setKnown(null); setRedeem(NO_REDEEM); setAddr({}); setLoc(NO_LOC); setPaying(false); };
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const k = e.key.toLowerCase();
      if (k === "b") { e.preventDefault(); document.getElementById("order-product-search")?.focus(); }
      if (k === "p") { e.preventDefault(); payRef.current(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  const delivery = f.type === "delivery";
  const mapMode = mapArea && delivery;
  const cur = mapArea ? loc.addr : addr;
  const zone = delivery && !mapArea ? (st.delivery_zones || []).find((z) => norm(z.name) === norm(cur.district)) : null;
  const fee = !delivery || !items.length ? 0 : mapArea && loc.quote?.ok ? loc.quote.fee : zone ? Number(zone.fee) : Number(st.delivery_fee || 0);
  const addLine = ({ product: p, options = [], qty = 1, notes = "", name, price }) => setItems((c) => {
    const key = `${p.id}|${JSON.stringify(options)}|${notes}`;
    return c.find((i) => i.key === key) ? c.map((i) => (i.key === key ? { ...i, qty: i.qty + qty } : i))
      : [...c, { key, id: p.id, name: name || p.name, price: price ?? p.price, qty, options, notes }];
  });
  const add = (p, qty) => (p.option_groups?.length ? setPicking(p) : addLine({ product: p, qty }));
  const setQty = (key, qty) => setItems((c) => c.map((i) => (i.key === key ? { ...i, qty } : i)).filter((i) => i.qty > 0));
  const fillKnown = (c) => {
    setF((x) => ({ ...x, customer: { ...x.customer, name: x.customer.name || c.name } }));
    const a = c.addr && Object.keys(c.addr).length ? c.addr : c.address ? { street: c.address } : null;
    if (a) { setAddr((x) => (x.street ? x : a)); setLoc((l) => (l.addr.street ? l : { ...NO_LOC, addr: a })); }
  };
  const setType = (v) => {
    setF((x) => ({ ...x, type: v }));
    if (v === "delivery" && !cur.street) setAddrOpen(true);
  };
  const subtotal = items.reduce((s, i) => s + i.price * i.qty, 0);
  const lo = loyaltyCalc(known, redeem, subtotal, fee);
  const lmode = known?.found && known.loyalty?.enabled ? known.loyalty.mode : "";
  const total = subtotal + fee - lo.off - lo.deliveryOff;
  const block = blockOf(items, f, delivery && !cur.street);
  payRef.current = () => (block ? toast.error(block) : setPaying(true));
  const discard = () => {
    if (items.length && !window.confirm("Descartar este pedido?")) return;
    reset();
    onOpenChange(false);
  };
  const submit = async ({ payment_method, change_for }) => {
    const where = delivery ? { address: addrText(cur), addr: cur, ...(mapMode && loc.point ? { lat: loc.point[0], lng: loc.point[1] } : {}) } : {};
    try {
      await api.post("/orders", { ...f, zone: zone?.name || "", customer: { ...f.customer, name: nameOf(f), ...where }, payment_method, change_for, use_loyalty: lmode === "cashback" && lo.off > 0,
        loyalty_rewards: lmode === "points" ? redeem.sel : [], loyalty_free_delivery: lmode === "points" && redeem.freeDel && delivery,
        items: items.map((i) => ({ product_id: i.id, qty: i.qty, options: i.options, notes: i.notes })) });
      toast.success("Pedido criado");
      reset();
      onOpenChange(false);
      onCreated();
    } catch (e) {
      toast.error(errMsg(e));
      throw e;
    }
  };
  const hasCustomer = !!(f.customer.name || f.customer.phone);
  return (
    <>
      {!open && items.length > 0 && <DraftPill count={items.reduce((s, i) => s + i.qty, 0)} total={total} onOpen={() => onOpenChange(true)} />}
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex h-[94vh] max-h-[94vh] w-[98vw] max-w-[1700px] flex-col gap-0 overflow-hidden p-0 [&>button.absolute]:hidden" data-testid="new-order-modal">
          <DialogTitle className="sr-only">Novo pedido</DialogTitle>
          <div className="grid min-h-0 flex-1 auto-rows-max grid-cols-1 overflow-y-auto lg:auto-rows-auto lg:grid-cols-[minmax(0,1fr)_340px] lg:overflow-hidden xl:grid-cols-[minmax(0,1fr)_400px]">
            <div className="order-2 h-[65vh] min-h-0 border-t lg:order-1 lg:h-full lg:border-t-0" data-testid="order-products-column"><ProductPicker onPick={add} panel /></div>
            <aside className="order-1 flex min-h-0 flex-col bg-card lg:order-2 lg:border-l" data-testid="order-side-panel">
              <header className="flex h-16 shrink-0 items-center justify-between border-b pl-5 pr-3">
                <h2 className="text-lg font-semibold">Novo pedido</h2>
                <span className="flex gap-1">
                  <HeadBtn onClick={() => onOpenChange(false)} label="Minimizar (o pedido fica guardado)" testId="order-minimize-btn"><Minus className="h-5 w-5" /></HeadBtn>
                  <HeadBtn onClick={discard} label="Fechar e descartar" testId="order-close-btn"><X className="h-5 w-5" /></HeadBtn>
                </span>
              </header>
              <div className="min-h-0 space-y-3 p-4 lg:flex-1 lg:overflow-y-auto">
                <CustomerSearch customer={f.customer} setCustomer={(c) => setF((x) => ({ ...x, customer: c }))} onPick={fillKnown} />
                {hasCustomer && <OrderCustomerBox phone={f.customer.phone} known={known} setKnown={setKnown} onFound={fillKnown} redeem={redeem} setRedeem={setRedeem} allowFreeDelivery={delivery} />}
                <Select value={f.type} onValueChange={setType}>
                  <SelectTrigger className="h-14 px-4" data-testid="order-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map(([v, l, Icon]) => <SelectItem key={v} value={v} data-testid={`order-type-${v}`}><span className="flex items-center gap-3"><Icon className="h-5 w-5 text-muted-foreground" />{l}</span></SelectItem>)}</SelectContent>
                </Select>
                {f.type === "mesa" && <Input value={f.table_number} onChange={(e) => setF((x) => ({ ...x, table_number: e.target.value }))} placeholder="Número da mesa" className="h-12" data-testid="order-table-input" />}
                {delivery && <AddressRow addr={cur} quote={mapMode ? loc.quote : zone ? { ok: true, area: zone.name, fee } : null} onOpen={() => setAddrOpen(true)} />}
                <NotesBox notes={f.notes} setNotes={(v) => setF((x) => ({ ...x, notes: v }))} />
                <div className="flex items-center justify-between px-1 pt-3">
                  <p className="text-lg font-semibold">Carrinho</p>
                  {items.length > 0 && <button type="button" onClick={() => setItems([])} className="text-xs font-medium uppercase tracking-wide text-muted-foreground transition-colors hover:text-foreground" data-testid="order-cart-clear">Limpar</button>}
                </div>
                <NewOrderCart items={items} setQty={setQty} lo={lo} lmode={lmode} freeDel={redeem.freeDel && delivery} />
              </div>
              <CartFooter subtotal={subtotal} fee={fee} off={lo.off + lo.deliveryOff} total={total} onPay={() => payRef.current()} />
            </aside>
          </div>
          <OptionsDialog product={picking} onClose={() => setPicking(null)} onAdd={addLine} />
          <AddressDialog open={addrOpen} onClose={() => setAddrOpen(false)} value={addr} loc={loc} mapMode={mapArea}
            onSave={(v) => { if (mapArea) setLoc(v); else setAddr(v); setAddrOpen(false); }} />
          <OrderPayDialog open={paying} onClose={() => setPaying(false)} total={total} onConfirm={submit} />
        </DialogContent>
      </Dialog>
    </>
  );
}
